import * as THREE from 'three';

/**
 * 二维有限线段定义。
 */
export interface LineLineIntersectionDefinition2D {
    /** 线段起点。 */
    start: THREE.Vector2;
    /** 线段终点。 */
    end: THREE.Vector2;
}

/**
 * 两条支撑线的单个真实交点。
 */
export interface LineLinePointIntersection2D {
    /** 二维交点坐标。 */
    point: THREE.Vector2;
    /** 交点在第一条线参数方程上的参数。 */
    firstParameter: number;
    /** 交点在第二条线参数方程上的参数。 */
    secondParameter: number;
}

/**
 * 两条共线有限线段的正长度重叠区间。
 */
export interface LineLineOverlapIntersection2D {
    /** 重叠区间在第一条线方向上的起点。 */
    startPoint: THREE.Vector2;
    /** 重叠区间在第一条线方向上的终点。 */
    endPoint: THREE.Vector2;
    /** 重叠起点在第一条线上的参数。 */
    firstStartParameter: number;
    /** 重叠终点在第一条线上的参数。 */
    firstEndParameter: number;
    /** 重叠起点在第二条线上的参数。 */
    secondStartParameter: number;
    /** 重叠终点在第二条线上的参数。 */
    secondEndParameter: number;
}

/**
 * 两条无限支撑线的求交结果。
 */
export type SupportingLineIntersectionResult2D =
    | {relation: 'point'; intersection: LineLinePointIntersection2D}
    | {relation: 'parallel'}
    | {relation: 'coincident'};

/**
 * 两条有限线段的求交结果。
 */
export type LineSegmentIntersectionResult2D =
    | {relation: 'none'}
    | {relation: 'point'; intersection: LineLinePointIntersection2D}
    | {relation: 'overlap'; overlap: LineLineOverlapIntersection2D};

/**
 * 二维直线—直线真实交点求解器。
 *
 * 职责：
 * 1. 使用尺度归一化行列式求解两条无限支撑线；
 * 2. 明确区分唯一交点、平行和完全共线；
 * 3. 在有限线段模式下过滤参数域，并把正长度共线公共区间作为 overlap 返回；
 * 4. 保留双方参数，供裁剪、延伸和拖动路径按各自语义消费。
 */
export class LineLineIntersectionSolver {
    /** 支撑线方向和平行关系的相对容差。 */
    private static readonly RELATIVE_TOLERANCE = 1e-12;

    /** 有限线段参数域容差。 */
    private static readonly PARAMETER_TOLERANCE = 1e-10;

    /**
     * 求解两条无限支撑线。
     *
     * @param firstLine 第一条线定义。
     * @param secondLine 第二条线定义。
     * @returns 唯一交点、平行或共线关系。
     */
    public solveSupportingLines(
        firstLine: LineLineIntersectionDefinition2D,
        secondLine: LineLineIntersectionDefinition2D,
    ): SupportingLineIntersectionResult2D {
        const firstDirection = firstLine.end.clone().sub(firstLine.start);
        const secondDirection = secondLine.end.clone().sub(secondLine.start);
        const offset = secondLine.start.clone().sub(firstLine.start);
        const geometryScale = Math.max(
            firstDirection.length(),
            secondDirection.length(),
            offset.length(),
            Number.MIN_VALUE,
        );

        if (!this.hasValidDefinition(firstLine, secondLine, geometryScale)) {
            console.error('[LineLineIntersectionSolver] 直线定义无效或退化，无法计算真实交点。', {
                firstLine,
                secondLine,
            });
            return {relation: 'parallel'};
        }

        // 所有向量先除以同一特征长度；参数比值不变，同时避免小尺度叉积被固定容差吞掉。
        const normalizedFirstDirection = firstDirection.clone().multiplyScalar(1 / geometryScale);
        const normalizedSecondDirection = secondDirection.clone().multiplyScalar(1 / geometryScale);
        const normalizedOffset = offset.clone().multiplyScalar(1 / geometryScale);
        const denominator = this.cross(normalizedFirstDirection, normalizedSecondDirection);
        const denominatorTolerance = normalizedFirstDirection.length()
            * normalizedSecondDirection.length()
            * LineLineIntersectionSolver.RELATIVE_TOLERANCE;

        if (Math.abs(denominator) <= denominatorTolerance) {
            // 平行方向还需检查第二条线起点是否位于第一条支撑线上，以区分平行和完全共线。
            const collinearResidual = Math.abs(this.cross(normalizedOffset, normalizedFirstDirection));
            const collinearScale = Math.max(
                normalizedOffset.length() * normalizedFirstDirection.length(),
                Number.EPSILON,
            );
            return collinearResidual <= collinearScale * LineLineIntersectionSolver.RELATIVE_TOLERANCE
                ? {relation: 'coincident'}
                : {relation: 'parallel'};
        }

        const firstParameter = this.cross(normalizedOffset, normalizedSecondDirection) / denominator;
        const secondParameter = this.cross(normalizedOffset, normalizedFirstDirection) / denominator;
        if (!Number.isFinite(firstParameter) || !Number.isFinite(secondParameter)) {
            console.error('[LineLineIntersectionSolver] 直线交点参数不是有限数。', {
                firstLine,
                secondLine,
            });
            return {relation: 'parallel'};
        }

        return {
            relation: 'point',
            intersection: {
                point: firstLine.start.clone().addScaledVector(firstDirection, firstParameter),
                firstParameter,
                secondParameter,
            },
        };
    }

    /**
     * 求解两条有限线段。
     *
     * @param firstLine 第一条有限线段。
     * @param secondLine 第二条有限线段。
     * @returns 无交点、唯一交点或正长度重叠区间。
     */
    public solveSegments(
        firstLine: LineLineIntersectionDefinition2D,
        secondLine: LineLineIntersectionDefinition2D,
    ): LineSegmentIntersectionResult2D {
        const supportResult = this.solveSupportingLines(firstLine, secondLine);
        if (supportResult.relation === 'parallel') {
            return {relation: 'none'};
        }

        if (supportResult.relation === 'point') {
            const {intersection} = supportResult;
            if (
                !this.isParameterInSegment(intersection.firstParameter)
                || !this.isParameterInSegment(intersection.secondParameter)
            ) {
                return {relation: 'none'};
            }
            return {
                relation: 'point',
                intersection: {
                    point: intersection.point,
                    firstParameter: THREE.MathUtils.clamp(intersection.firstParameter, 0, 1),
                    secondParameter: THREE.MathUtils.clamp(intersection.secondParameter, 0, 1),
                },
            };
        }

        return this.solveCoincidentSegments(firstLine, secondLine);
    }

    /**
     * 求解两条共线有限线段的公共域。
     * @param firstLine 第一条线段。
     * @param secondLine 第二条线段。
     * @returns 无交点、端点单交或正长度重叠。
     */
    private solveCoincidentSegments(
        firstLine: LineLineIntersectionDefinition2D,
        secondLine: LineLineIntersectionDefinition2D,
    ): LineSegmentIntersectionResult2D {
        const firstDirection = firstLine.end.clone().sub(firstLine.start);
        const firstLengthSquared = firstDirection.lengthSq();
        const secondStartOnFirst = secondLine.start.clone().sub(firstLine.start).dot(firstDirection)
            / firstLengthSquared;
        const secondEndOnFirst = secondLine.end.clone().sub(firstLine.start).dot(firstDirection)
            / firstLengthSquared;
        const firstStartParameter = Math.max(0, Math.min(secondStartOnFirst, secondEndOnFirst));
        const firstEndParameter = Math.min(1, Math.max(secondStartOnFirst, secondEndOnFirst));

        if (firstEndParameter < firstStartParameter - LineLineIntersectionSolver.PARAMETER_TOLERANCE) {
            return {relation: 'none'};
        }

        const normalizedStartParameter = THREE.MathUtils.clamp(firstStartParameter, 0, 1);
        const normalizedEndParameter = THREE.MathUtils.clamp(firstEndParameter, 0, 1);
        const startPoint = firstLine.start.clone().addScaledVector(firstDirection, normalizedStartParameter);
        const endPoint = firstLine.start.clone().addScaledVector(firstDirection, normalizedEndParameter);
        const secondDirection = secondLine.end.clone().sub(secondLine.start);
        const secondLengthSquared = secondDirection.lengthSq();
        const secondStartParameter = startPoint.clone().sub(secondLine.start).dot(secondDirection)
            / secondLengthSquared;
        const secondEndParameter = endPoint.clone().sub(secondLine.start).dot(secondDirection)
            / secondLengthSquared;

        // 零长度公共域是一个真实端点交点，不能伪装成 overlap。
        if (
            normalizedEndParameter - normalizedStartParameter
            <= LineLineIntersectionSolver.PARAMETER_TOLERANCE
        ) {
            return {
                relation: 'point',
                intersection: {
                    point: startPoint,
                    firstParameter: normalizedStartParameter,
                    secondParameter: THREE.MathUtils.clamp(secondStartParameter, 0, 1),
                },
            };
        }

        return {
            relation: 'overlap',
            overlap: {
                startPoint,
                endPoint,
                firstStartParameter: normalizedStartParameter,
                firstEndParameter: normalizedEndParameter,
                secondStartParameter: THREE.MathUtils.clamp(secondStartParameter, 0, 1),
                secondEndParameter: THREE.MathUtils.clamp(secondEndParameter, 0, 1),
            },
        };
    }

    /**
     * 判断两个线定义是否有效。
     * @param firstLine 第一条线。
     * @param secondLine 第二条线。
     * @param geometryScale 当前特征长度。
     * @returns 坐标有限且双方方向非退化时返回 true。
     */
    private hasValidDefinition(
        firstLine: LineLineIntersectionDefinition2D,
        secondLine: LineLineIntersectionDefinition2D,
        geometryScale: number,
    ): boolean {
        const coordinates = [
            firstLine.start.x,
            firstLine.start.y,
            firstLine.end.x,
            firstLine.end.y,
            secondLine.start.x,
            secondLine.start.y,
            secondLine.end.x,
            secondLine.end.y,
        ];
        const minimumLength = geometryScale * Number.EPSILON;
        return coordinates.every((coordinate) => Number.isFinite(coordinate))
            && firstLine.start.distanceTo(firstLine.end) > minimumLength
            && secondLine.start.distanceTo(secondLine.end) > minimumLength;
    }

    /**
     * 判断参数是否落在有限线段域内。
     * @param parameter 待判断参数。
     * @returns 位于 `[0,1]` 容差域内时返回 true。
     */
    private isParameterInSegment(parameter: number): boolean {
        return parameter >= -LineLineIntersectionSolver.PARAMETER_TOLERANCE
            && parameter <= 1 + LineLineIntersectionSolver.PARAMETER_TOLERANCE;
    }

    /**
     * 计算两个二维向量的标量叉积。
     * @param first 第一个向量。
     * @param second 第二个向量。
     * @returns 标量叉积。
     */
    private cross(first: THREE.Vector2, second: THREE.Vector2): number {
        return first.x * second.y - first.y * second.x;
    }
}
